import React, { useState, useEffect } from 'react';
import './Filters.css';
import { DEVICES } from '../../constants/breakpoints';

const Filters = ({
  selectedElement,
  updateElementStyles,
  selectedDevice
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState({
    blur: 0,
    brightness: 100,
    contrast: 100,
    grayscale: 0,
    hueRotate: 0,
    invert: 0,
    opacity: 100,
    saturate: 100,
    sepia: 0,
    dropShadow: {
      offsetX: 0,
      offsetY: 4,
      blur: 4,
      color: '#000000'
    }
  });
  const [expandedFilters, setExpandedFilters] = useState({
    dropShadow: false,
    blur: false,
    brightness: false,
    contrast: false,
    grayscale: false,
    hueRotate: false,
    invert: false,
    opacity: false,
    saturate: false,
    sepia: false
  });

  useEffect(() => {
    if (selectedElement?.styles) {
      let filterString;

      // Get filter based on device cascade
      if (selectedDevice === DEVICES.MOBILE) {
        filterString = selectedElement.styles?.[DEVICES.MOBILE]?.filter ||
                      selectedElement.styles?.[DEVICES.TABLET]?.filter ||
                      selectedElement.styles?.[DEVICES.DESKTOP]?.filter ||
                      selectedElement.styles?.filter;
      } else if (selectedDevice === DEVICES.TABLET) {
        filterString = selectedElement.styles?.[DEVICES.TABLET]?.filter ||
                      selectedElement.styles?.[DEVICES.DESKTOP]?.filter ||
                      selectedElement.styles?.filter;
      } else {
        filterString = selectedElement.styles?.[DEVICES.DESKTOP]?.filter ||
                      selectedElement.styles?.filter;
      }

      if (filterString) {
        const dropShadowMatch = filterString.match(/drop-shadow\(([^)]+)\)/);
        const dropShadow = dropShadowMatch ? {
          offsetX: parseInt(dropShadowMatch[1].split(' ')[0]) || 0,
          offsetY: parseInt(dropShadowMatch[1].split(' ')[1]) || 4,
          blur: parseInt(dropShadowMatch[1].split(' ')[2]) || 4,
          color: dropShadowMatch[1].split(' ')[3] || '#000000'
        } : {
          offsetX: 0,
          offsetY: 4,
          blur: 4,
          color: '#000000'
        };

        const newFilters = {
          blur: parseInt(filterString.match(/blur\((\d+)px\)/)?.[1] || 0),
          brightness: parseInt(filterString.match(/brightness\((\d+)%\)/)?.[1] || 100),
          contrast: parseInt(filterString.match(/contrast\((\d+)%\)/)?.[1] || 100),
          grayscale: parseInt(filterString.match(/grayscale\((\d+)%\)/)?.[1] || 0),
          hueRotate: parseInt(filterString.match(/hue-rotate\((\d+)deg\)/)?.[1] || 0),
          invert: parseInt(filterString.match(/invert\((\d+)%\)/)?.[1] || 0),
          opacity: parseInt(filterString.match(/opacity\((\d+)%\)/)?.[1] || 100),
          saturate: parseInt(filterString.match(/saturate\((\d+)%\)/)?.[1] || 100),
          sepia: parseInt(filterString.match(/sepia\((\d+)%\)/)?.[1] || 0),
          dropShadow
        };
        setFilters(newFilters);
      }
    }
  }, [selectedElement, selectedDevice]);

  const handleFilterChange = (property, value) => {
    const newFilters = { ...filters, [property]: value };
    setFilters(newFilters);
    updateFilters(newFilters);
  };

  const handleDropShadowChange = (property, value) => {
    const newDropShadow = { ...filters.dropShadow, [property]: value };
    const newFilters = { ...filters, dropShadow: newDropShadow };
    setFilters(newFilters);
    updateFilters(newFilters);
  };

  const updateFilters = (newFilters) => {
    const { dropShadow, ...otherFilters } = newFilters;
    const dropShadowString = `drop-shadow(${dropShadow.offsetX}px ${dropShadow.offsetY}px ${dropShadow.blur}px ${dropShadow.color})`;
    
    const filterString = `
      ${dropShadowString}
      blur(${otherFilters.blur}px)
      brightness(${otherFilters.brightness}%)
      contrast(${otherFilters.contrast}%)
      grayscale(${otherFilters.grayscale}%)
      hue-rotate(${otherFilters.hueRotate}deg)
      invert(${otherFilters.invert}%)
      opacity(${otherFilters.opacity}%)
      saturate(${otherFilters.saturate}%)
      sepia(${otherFilters.sepia}%)
    `.replace(/\s+/g, ' ').trim();

    updateElementStyles(
      selectedElement.id,
      "filter",
      filterString,
      null,
      selectedDevice
    );
  };

  const toggleFilter = (filterName) => {
    setExpandedFilters(prev => ({
      ...prev,
      [filterName]: !prev[filterName]
    }));
  };

  return (
    <div className="filters-section">
      <div className="section-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="section-title">
          <i className="fas fa-magic"></i>
          <span>Filters</span>
        </div>
        <div className="section-controls">
          <i className={`fas fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
        </div>
      </div>

      {isExpanded && (
        <div className="filters-controls">
          <div className="filter-item">
            <div 
              className="filter-header"
              onClick={() => toggleFilter('dropShadow')}
            >
              <span className="filter-title">Drop Shadow</span>
              <i className={`fas fa-chevron-${expandedFilters.dropShadow ? 'down' : 'right'}`}></i>
            </div>
            {expandedFilters.dropShadow && (
              <div className="drop-shadow-inputs">
                <div className="drop-shadow-sliders">
                  <div className="slider-group">
                    <label>X</label>
                    <div className="slider-input-group">
                      <input
                        type="range"
                        min="-50"
                        max="50"
                        value={filters.dropShadow.offsetX}
                        onChange={(e) => handleDropShadowChange('offsetX', parseInt(e.target.value))}
                      />
                      <input
                        type="number"
                        value={filters.dropShadow.offsetX}
                        onChange={(e) => handleDropShadowChange('offsetX', parseInt(e.target.value) || 0)}
                      />
                      <span className="unit">px</span>
                    </div>
                  </div>
                  <div className="slider-group">
                    <label>Y</label>
                    <div className="slider-input-group">
                      <input
                        type="range"
                        min="-50"
                        max="50"
                        value={filters.dropShadow.offsetY}
                        onChange={(e) => handleDropShadowChange('offsetY', parseInt(e.target.value))}
                      />
                      <input
                        type="number"
                        value={filters.dropShadow.offsetY}
                        onChange={(e) => handleDropShadowChange('offsetY', parseInt(e.target.value) || 0)}
                      />
                      <span className="unit">px</span>
                    </div>
                  </div>
                  <div className="slider-group">
                    <label>Blur</label>
                    <div className="slider-input-group">
                      <input
                        type="range"
                        min="0"
                        max="50"
                        value={filters.dropShadow.blur}
                        onChange={(e) => handleDropShadowChange('blur', parseInt(e.target.value))}
                      />
                      <input
                        type="number"
                        value={filters.dropShadow.blur}
                        onChange={(e) => handleDropShadowChange('blur', parseInt(e.target.value) || 0)}
                      />
                      <span className="unit">px</span>
                    </div>
                  </div>
                </div>
                <div className="color-input-group">
                  <label>Color</label>
                  <div className="color-inputs">
                    <input
                      type="color"
                      value={filters.dropShadow.color}
                      onChange={(e) => handleDropShadowChange('color', e.target.value)}
                    />
                    <input
                      type="text"
                      value={filters.dropShadow.color}
                      onChange={(e) => {
                        const newColor = e.target.value;
                        if (newColor.match(/^#[0-9A-Fa-f]{0,6}$/)) {
                          handleDropShadowChange('color', newColor);
                        }
                      }}
                      placeholder="#000000"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {Object.entries({
            blur: { min: 0, max: 20, unit: 'px', label: 'Blur' },
            brightness: { min: 0, max: 200, unit: '%', label: 'Brightness' },
            contrast: { min: 0, max: 200, unit: '%', label: 'Contrast' },
            grayscale: { min: 0, max: 100, unit: '%', label: 'Grayscale' },
            hueRotate: { min: 0, max: 360, unit: 'deg', label: 'Hue Rotate' },
            invert: { min: 0, max: 100, unit: '%', label: 'Invert' },
            opacity: { min: 0, max: 100, unit: '%', label: 'Opacity' },
            saturate: { min: 0, max: 200, unit: '%', label: 'Saturate' },
            sepia: { min: 0, max: 100, unit: '%', label: 'Sepia' }
          }).map(([key, config]) => (
            <div key={key} className="filter-item">
              <div 
                className="filter-header"
                onClick={() => toggleFilter(key)}
              >
                <span className="filter-title">{config.label}</span>
                <div className="filter-preview">
                  <span className="filter-value">{filters[key]}{config.unit}</span>
                  <i className={`fas fa-chevron-${expandedFilters[key] ? 'down' : 'right'}`}></i>
                </div>
              </div>
              {expandedFilters[key] && (
                <div className="filter-content">
                  <div className="slider-input-group">
                    <input
                      type="range"
                      min={config.min}
                      max={config.max}
                      value={filters[key]}
                      onChange={(e) => handleFilterChange(key, parseInt(e.target.value))}
                    />
                    <input
                      type="number"
                      min={config.min}
                      max={config.max}
                      value={filters[key]}
                      onChange={(e) => handleFilterChange(key, parseInt(e.target.value) || 0)}
                    />
                    <span className="unit">{config.unit}</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Filters; 